import { z } from 'zod';

/**
 * Radar frames, normalized from RainViewer's public weather-maps feed.
 *
 * RainViewer publishes a rolling window of composite radar mosaics — roughly
 * two hours of observed frames, plus (where coverage allows) a short nowcast
 * extrapolation. Each frame is a full tile pyramid addressed by an opaque
 * `path`; the Worker flattens past + nowcast into one ordered list so the
 * client just walks an array.
 */

/** Whether a frame is observed radar or a forecast extrapolation. */
export const radarFrameKindSchema = z.enum(['past', 'nowcast']);
export type RadarFrameKind = z.infer<typeof radarFrameKindSchema>;

export const radarFrameSchema = z.object({
  /** Frame valid time, as a Unix timestamp in seconds (RainViewer's native unit). */
  time: z.number().int().positive(),
  /** Opaque tile-pyramid path, e.g. `/v2/radar/a92f0aecfc6e`. */
  path: z.string().min(1),
  kind: radarFrameKindSchema,
});
export type RadarFrame = z.infer<typeof radarFrameSchema>;

export const radarFramesResponseSchema = z.object({
  /** When RainViewer generated this feed (Unix seconds). */
  generated: z.number().int().positive(),
  /** Tile host to build frame URLs against, e.g. `https://tilecache.rainviewer.com`. */
  host: z.url(),
  /** Observed frames first, then nowcast — both ascending by time. */
  frames: z.array(radarFrameSchema),
});
export type RadarFramesResponse = z.infer<typeof radarFramesResponseSchema>;

/**
 * The colour-scheme id RainViewer's tile URLs carry.
 *
 * **RainViewer ignores it.** Their documented API takes a scheme id from 1 to 8,
 * but every one of them returns a byte-identical tile: verified by md5 across
 * all eight ids, on more than one tile, at both 256 and 512, against
 * `tilecache.rainviewer.com` directly as well as through our proxy. What they
 * actually render is the ramp below — pale cyan through blue to a dark navy,
 * then jumping to yellow, orange and red — which is their "Universal Blue",
 * id 2. The id is pinned to 2 so the URL at least names what comes back.
 *
 * This used to be a picker. It changed the legend and the slider track and
 * nothing else, which made the legend actively wrong: choosing "NEXRAD Level
 * III" promised bright green for light returns that render cyan. A control that
 * cannot move the thing it names is worse than no control, so it was removed
 * rather than left to imply a choice nobody has.
 *
 * If RainViewer ever honours the parameter, this is the one place to unpin —
 * but check what the tiles actually contain before trusting the documentation
 * again, and re-match `radar-legend.ts` to the pixels.
 */
export const RAINVIEWER_COLOR_SCHEME = 2;

export const radarTileOptionsSchema = z.object({
  colorScheme: z.literal(RAINVIEWER_COLOR_SCHEME).default(RAINVIEWER_COLOR_SCHEME),
  /** Interpolate between reflectivity steps instead of hard banding. */
  smooth: z.boolean().default(true),
  /** Render frozen precipitation in its own palette. */
  showSnow: z.boolean().default(true),
  /** RainViewer serves 256px and 512px pyramids; 512 is crisper on retina. */
  tileSize: z.union([z.literal(256), z.literal(512)]).default(512),
});
export type RadarTileOptions = z.infer<typeof radarTileOptionsSchema>;

export const DEFAULT_RADAR_TILE_OPTIONS: RadarTileOptions = radarTileOptionsSchema.parse({});

/**
 * A frame's opaque content id — the last segment of its path, e.g.
 * `a92f0aecfc6e` from `/v2/radar/a92f0aecfc6e`.
 *
 * RainViewer addresses each frame by a hash of its contents, which is what
 * makes a tile URL immutable and therefore safe to cache indefinitely.
 */
export function radarFrameId(frame: Pick<RadarFrame, 'path'>): string {
  return frame.path.split('/').filter(Boolean).pop() ?? '';
}

/** Path prefix of the Worker's tile proxy. */
export const RADAR_TILE_PATH = '/api/radar/tile';

/** Path prefix of the Worker's radar-coverage proxy. */
export const RADAR_COVERAGE_PATH = '/api/radar/coverage';

/**
 * Highest zoom RainViewer serves radar tiles at.
 *
 * Past this they do not 404 — they answer `200 OK` with a grey placeholder PNG
 * reading "Zoom Level Not Supported", which paints over the map as a repeating
 * grid of error cards. It is the same trap as their rate-limit response: a
 * failure delivered as a successful image, so nothing downstream can tell it
 * apart from radar without knowing this limit up front.
 *
 * Both edges enforce it. The client caps the raster source's `maxzoom` so
 * MapLibre overzooms the last real tile instead of requesting one that does not
 * exist; the proxy rejects the path outright, so a stale client cannot pin a
 * placeholder into the edge cache for a day.
 */
export const RAINVIEWER_MAX_ZOOM = 7;

/**
 * Builds the XYZ tile template for one frame, in the `{z}/{x}/{y}` form MapLibre
 * expects.
 *
 * Tiles are addressed on our own origin and proxied by the Worker rather than
 * fetched from RainViewer directly. RainViewer rate-limits per client, and a
 * radar loop is inherently tile-hungry — every frame needs its own copy of the
 * viewport. Going through the Worker means a given tile is fetched from
 * RainViewer once and then served from cache to every subsequent request.
 *
 *   /api/radar/tile/{frameId}/{size}/{z}/{x}/{y}/{color}/{smooth}_{snow}.png
 */
export function radarTileUrl(
  frame: Pick<RadarFrame, 'path'>,
  options: RadarTileOptions = DEFAULT_RADAR_TILE_OPTIONS,
): string {
  const { tileSize, colorScheme, smooth, showSnow } = options;
  const flags = `${smooth ? 1 : 0}_${showSnow ? 1 : 0}`;
  return `${RADAR_TILE_PATH}/${radarFrameId(frame)}/${tileSize}/{z}/{x}/{y}/${colorScheme}/${flags}.png`;
}

/**
 * Builds the XYZ tile template for RainViewer's radar-coverage mask.
 *
 * The mask answers a question the radar frames cannot: whether an area with no
 * precipitation drawn on it is *dry*, or merely *unobserved*. RainViewer is a
 * mosaic of national radar networks, so its coverage is a union of range rings
 * with real gaps between them — over oceans, and over countries with no public
 * radar feed. A clear map inside a gap looks exactly like a clear map inside
 * coverage, which invites a viewer to read "no radar here" as "no rain here".
 *
 * The mask is already shaped for this: covered pixels are fully transparent and
 * uncovered pixels are opaque black, so drawing it at partial opacity dims the
 * gaps and leaves observed ground untouched. No recolouring needed.
 *
 *   /api/radar/coverage/{size}/{z}/{x}/{y}.png
 */
export function radarCoverageTileUrl(
  tileSize: RadarTileOptions['tileSize'] = DEFAULT_RADAR_TILE_OPTIONS.tileSize,
): string {
  return `${RADAR_COVERAGE_PATH}/${tileSize}/{z}/{x}/{y}.png`;
}
